import { Button } from '@sea-snag/ui';
import confetti from 'canvas-confetti';
import { Monitor, Moon, Sun } from 'lucide-react';
import { useState } from 'react';

import { useTheme } from '../../hooks/use-theme';

const themes = [
  {
    value: 'light' as const,
    label: 'Light',
    icon: Sun,
    previewBg: 'bg-white',
    previewSidebar: 'bg-gray-100',
    previewText: 'bg-gray-300',
    previewAccent: 'bg-gray-200',
  },
  {
    value: 'dark' as const,
    label: 'Dark',
    icon: Moon,
    previewBg: 'bg-gray-900',
    previewSidebar: 'bg-gray-800',
    previewText: 'bg-gray-600',
    previewAccent: 'bg-gray-700',
  },
  {
    value: 'system' as const,
    label: 'System',
    icon: Monitor,
    previewBg: 'bg-gradient-to-br from-white to-gray-900',
    previewSidebar: 'bg-gradient-to-b from-gray-100 to-gray-800',
    previewText: 'bg-gray-400',
    previewAccent: 'bg-gray-400',
  },
];

interface StepThemePickerProps {
  onComplete: () => void;
}

export function StepThemePicker({ onComplete }: StepThemePickerProps) {
  const { theme, setTheme } = useTheme();
  const [completing, setCompleting] = useState(false);

  const handleComplete = () => {
    setCompleting(true);

    // Fire confetti
    void confetti({
      particleCount: 150,
      spread: 80,
      origin: { y: 0.6 },
      colors: ['#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b'],
    });

    // Redirect after celebration
    setTimeout(() => {
      onComplete();
    }, 1500);
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold">Choose your theme</h3>
        <p className="text-sm text-muted-foreground">
          Pick a look that suits you. You can change this anytime in settings.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {themes.map((t) => {
          const Icon = t.icon;
          const isSelected = theme === t.value;

          return (
            <button
              key={t.value}
              type="button"
              onClick={() => setTheme(t.value)}
              className={`group flex flex-col items-center gap-3 rounded-xl border-2 p-4 transition-all duration-200 ${
                isSelected
                  ? 'border-primary bg-primary/5 shadow-sm'
                  : 'border-border hover:border-primary/50'
              }`}
            >
              {/* Mini preview */}
              <div className={`h-20 w-full overflow-hidden rounded-lg border ${t.previewBg}`}>
                <div className="flex h-full">
                  <div className={`w-1/4 ${t.previewSidebar}`}>
                    <div className={`mx-1 mt-2 h-1.5 rounded ${t.previewText}`} />
                    <div className={`mx-1 mt-1 h-1.5 rounded ${t.previewText}`} />
                    <div className={`mx-1 mt-1 h-1.5 rounded ${t.previewText}`} />
                  </div>
                  <div className="flex-1 p-2">
                    <div className={`h-2 w-3/4 rounded ${t.previewAccent}`} />
                    <div className={`mt-1.5 h-1.5 w-full rounded ${t.previewText}`} />
                    <div className={`mt-1 h-1.5 w-2/3 rounded ${t.previewText}`} />
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Icon
                  className={`h-4 w-4 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`}
                />
                <span
                  className={`text-sm font-medium ${isSelected ? 'text-primary' : 'text-foreground'}`}
                >
                  {t.label}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      <Button size="lg" className="w-full" onClick={handleComplete} disabled={completing}>
        {completing ? 'Setting up your workspace...' : 'Complete Setup'}
      </Button>
    </div>
  );
}
